import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Solution from "./components/Solution";
import Feature, { Benefits } from "./components/Feature";
import Testimonials from "./components/Testimonials";
import { Updates, FAQ } from "./components/PricingUpdatesFAQ";
import { CTASection, Footer } from "./components/CTAFooter";
import About from "./components/pages/About";
import SuccessStories from "./components/pages/SuccessStories";
import SuccessStoryDetail from "./components/pages/SuccessStoryDetail";
import Blogs from "./components/pages/Blogs";
import BlogDetail from "./components/pages/BlogDetail";
import Contact from "./components/pages/Contact";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import Sitemap from "./components/pages/Sitemap";
import BackToTop from "./components/BackToTop";
import CookieConsent from "./components/CookieConsent";
import NotFound from "./NotFound";

// Dashboard: auth
import { AuthProvider } from "./lib/AuthContext";
import Login from "./components/pages/dashboard/auth/Login";
import { RequireAuth } from "./components/pages/dashboard/auth/RequireAuth";
import DashboardHome from "./components/pages/dashboard/DashboardHome";

// Dashboard: agency
import AgencyLayout from "./components/pages/dashboard/agency/AgencyLayout";
import AgencyOverview from "./components/pages/dashboard/agency/AgencyOverview";
import Clients from "./components/pages/dashboard/agency/Clients";
import ClientDetail from "./components/pages/dashboard/agency/ClientDetail";
import Projects from "./components/pages/dashboard/agency/Projects";
import ProjectDetail from "./components/pages/dashboard/agency/ProjectDetail";
import AgencyInvoices from "./components/pages/dashboard/agency/Invoices";
import Team from "./components/pages/dashboard/agency/Team";
import ObjectiveTemplates from "./components/pages/dashboard/agency/ObjectiveTemplates";
import AuditLog from "./components/pages/dashboard/agency/AuditLog";
import AgencySettings from "./components/pages/dashboard/agency/Settings";
import Financials from "./components/pages/dashboard/agency/business/Financials";
import SalesPipeline from "./components/pages/dashboard/agency/business/SalesPipeline";
import ClientPortfolio from "./components/pages/dashboard/agency/business/ClientPortfolio";
import Services from "./components/pages/dashboard/agency/business/Services";
import CeoDashboard from "./components/pages/dashboard/agency/business/CeoDashboard";

// Dashboard: client
import ClientLayout from "./components/pages/dashboard/client/ClientLayout";
import ClientOverview from "./components/pages/dashboard/client/ClientOverview";
import ClientProjectDetail from "./components/pages/dashboard/client/ClientProjectDetail";
import ClientReports from "./components/pages/dashboard/client/ClientReports";
import ClientInvoices from "./components/pages/dashboard/client/ClientInvoices";
import ClientAccount from "./components/pages/dashboard/client/ClientAccount";

const Home = () => (
  <div className="min-h-screen bg-white font-sans text-neutral-900">
    <Navbar />
    <Hero />
    <Solution />
    <Feature />
    <Benefits />
    <Testimonials />
    <Updates />
    <FAQ />
    <CTASection />
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Existing site, unchanged */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/success-stories" element={<SuccessStories />} />
            <Route path="/success-stories/:slug" element={<SuccessStoryDetail />} />
            <Route path="/category/blogs" element={<Blogs />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/connect" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/sitemap" element={<Sitemap />} />

            {/* Dashboard: login is outside the auth guard, obviously */}
            <Route path="/dashboard/login" element={<Login />} />

            {/* Dashboard: entry point picks the right branch by role */}
            <Route path="/dashboard" element={<DashboardHome />} />

            {/* Dashboard: agency branch, admin and staff only */}
            <Route element={<RequireAuth allowedRoles={["agency_admin", "agency_staff"]} />}>
              <Route path="/dashboard/agency" element={<AgencyLayout />}>
                <Route index element={<AgencyOverview />} />
                <Route path="clients" element={<Clients />} />
                <Route path="clients/:clientId" element={<ClientDetail />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:projectId" element={<ProjectDetail />} />
                <Route path="invoices" element={<AgencyInvoices />} />
                <Route path="team" element={<Team />} />
                <Route path="objective-templates" element={<ObjectiveTemplates />} />
                <Route path="audit-log" element={<AuditLog />} />
                <Route path="settings" element={<AgencySettings />} />
                <Route path="business/financials" element={<Financials />} />
                <Route path="business/sales-pipeline" element={<SalesPipeline />} />
                <Route path="business/client-portfolio" element={<ClientPortfolio />} />
                <Route path="business/services" element={<Services />} />
                <Route path="business/ceo-dashboard" element={<CeoDashboard />} />
              </Route>
            </Route>

            {/* Dashboard: client branch, client role only */}
            <Route element={<RequireAuth allowedRoles={["client"]} />}>
              <Route path="/dashboard/client" element={<ClientLayout />}>
                <Route index element={<ClientOverview />} />
                <Route path="projects/:projectId" element={<ClientProjectDetail />} />
                <Route path="reports" element={<ClientReports />} />
                <Route path="invoices" element={<ClientInvoices />} />
                <Route path="account" element={<ClientAccount />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          <BackToTop />
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
